import { motion, AnimatePresence } from "framer-motion";
import { Gift, Copy, Check, Share2, ChevronRight, TrendingUp } from "lucide-react";

export const ProfileSocialTab = ({
  user,
  referral,
  referralCode,
  setReferralCode,
  applyingCode,
  applyReferral,
  copied,
  copyCode,
  shareCode,
  showHistory,
  setShowHistory,
  creditHistory,
}) => {
  return (
    <motion.div
      key="social"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      className="space-y-5"
    >
      {referral && (
        <div className="bg-card border border-border rounded-3xl p-5 space-y-4 shadow-sm" data-testid="referral-section">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#7000FF]" />
            <h3 className="font-heading font-bold text-sm text-card-foreground">Parrainage</h3>
          </div>

          <div className="bg-white border border-border rounded-2xl p-4">
            <p className="text-xs text-muted-foreground mb-2">Ton code de parrainage</p>
            <div className="flex items-center gap-2">
              <span
                className="flex-1 font-mono-credits font-bold text-lg text-card-foreground bg-muted rounded-xl px-4 py-2 text-center tracking-widest"
                data-testid="referral-code"
              >
                {referral.code}
              </span>
              <button
                onClick={copyCode}
                className={`p-2.5 rounded-xl transition-colors ${
                  copied ? "bg-emerald-50 text-emerald-500" : "bg-muted text-muted-foreground hover:text-card-foreground/80"
                }`}
                data-testid="copy-referral-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={shareCode}
                className="p-2.5 rounded-xl bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20 transition-colors"
                data-testid="share-referral-btn"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded-xl p-2.5 text-center border border-border">
              <p className="font-mono-credits font-bold text-base text-card-foreground" data-testid="referral-total">{referral.total_referrals}</p>
              <p className="text-[10px] text-muted-foreground">Invites</p>
            </div>
            <div className="bg-white rounded-xl p-2.5 text-center border border-border">
              <p className="font-mono-credits font-bold text-base text-emerald-500" data-testid="referral-completed">{referral.completed_referrals}</p>
              <p className="text-[10px] text-muted-foreground">Actifs</p>
            </div>
            <div className="bg-white rounded-xl p-2.5 text-center border border-border">
              <p className="font-mono-credits font-bold text-base text-[#FF6B35]" data-testid="referral-earned">{referral.credits_earned}</p>
              <p className="text-[10px] text-muted-foreground">Gagnes</p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Toi : +{referral.referrer_bonus} | Ton ami : +{referral.referred_bonus} credits
          </p>

          {referral.recent_referrals?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Recents</p>
              {referral.recent_referrals.map((r) => (
                <div key={r.pseudo || r.id} className="flex items-center gap-2 p-2 rounded-xl bg-muted">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center">
                    <span className="text-white text-[10px] font-bold">{(r.pseudo || "?")[0].toUpperCase()}</span>
                  </div>
                  <span className="text-xs font-medium text-card-foreground flex-1">{r.pseudo}</span>
                  <span className="text-[10px] text-emerald-500 font-mono-credits">+{referral.referrer_bonus}</span>
                </div>
              ))}
            </div>
          )}

          {!user.referred_by && (
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Tu as un code de parrainage ?</p>
              <div className="flex gap-2">
                <input
                  placeholder="Entrer un code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  className="flex-1 bg-white border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#7000FF] outline-none font-mono-credits tracking-wider"
                  data-testid="apply-referral-input"
                />
                <button
                  onClick={applyReferral}
                  disabled={applyingCode || !referralCode.trim()}
                  className="bg-[#7000FF] text-white font-bold px-4 rounded-xl text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                  data-testid="apply-referral-btn"
                >
                  {applyingCode ? "..." : "Appliquer"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full p-4 flex items-center justify-between"
          data-testid="credit-history-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <div className="text-left">
              <p className="font-heading font-bold text-sm text-card-foreground">Historique des credits</p>
              <p className="text-[10px] text-muted-foreground">Voir tes transactions</p>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showHistory ? "rotate-90" : ""}`} />
        </button>
        <AnimatePresence>
          {showHistory && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto"
            >
              {creditHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune transaction</p>
              ) : (
                creditHistory.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-2 border-t border-border">
                    <div>
                      <p className="text-sm text-card-foreground/80">{tx.source}</p>
                      <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <span className={`font-mono-credits font-bold text-sm ${tx.amount > 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {tx.amount > 0 ? "+" : ""}{tx.amount}
                    </span>
                  </div>
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
